import {
  BellRinging,
  ChartBar,
  Cube,
  HouseLine,
  ShoppingCart,
  Users,
  User,
  SpeakerHigh,
  GearSix,
  Storefront,
  Gauge,
  TrendUp,
  Waves,
  MagicWand,
  Factory,
  PhosphorLogo,
  ShoppingCartSimple,
  AlignBottom,
} from "phosphor-react";
import * as path from "../Routes/RoutePaths";

// Function to get current user permissions from localStorage (reads dynamically)
const getCurrentUserPermissions = () => {
  try {
    const userData = localStorage.getItem("userData");
    if (!userData) return [];
    const parsedUserData = JSON.parse(userData);
    return parsedUserData?.permissions || [];
  } catch (error) {
    console.error('Error parsing userData:', error);
    return [];
  }
};

// Map NavbarItems category names to permission category names
const categoryNameMap = {
  "Web_Settings": "Web Settings",
  "New_Arrival": "New Arrival",
  "P . Dashboard": "P . Dashboard"
};

// Helper function to normalize category name for permission matching
const normalizeCategoryName = (categoryName) => {
  return categoryNameMap[categoryName] || categoryName;
};

// Helper function to check if a permission exists for a given name and category
const hasPermission = (permissionName, categoryName, userPermissions) => {
  // Strict check: if no permissions array or empty, return false
  if (!userPermissions || !Array.isArray(userPermissions) || userPermissions.length === 0) {
    return false;
  }
  
  // If permissionName is not provided, return false
  if (!permissionName) {
    return false;
  }
  
  const normalizedCategory = normalizeCategoryName(categoryName);
  
  const hasMatch = userPermissions.some(perm => {
    // Ensure perm has required properties
    if (!perm || !perm.name || !perm.category) {
      return false;
    }
    
    // Check if permission name matches (case-insensitive)
    const nameMatch = perm.name?.toLowerCase().trim() === permissionName?.toLowerCase().trim();
    // Check if category matches (case-insensitive)
    const categoryMatch = perm.category?.toLowerCase().trim() === normalizedCategory?.toLowerCase().trim();
    
    return nameMatch && categoryMatch;
  });
  
  return hasMatch;
};

// Helper function to check if a single item (without sublinks) has permission
const hasSingleItemPermission = (categoryName, userPermissions) => {
  // Strict check: if no permissions, return false
  if (!userPermissions || !Array.isArray(userPermissions) || userPermissions.length === 0) {
    return false;
  }
  
  // For single items, check if any permission exists for this category
  // Special cases: "View Notification" for "Notification", "View Dashboard" for "Dashboard", etc.
  const specialCases = {
    "Notification": "View Notification",
    "Dashboard": "View Dashboard",
    "P . Dashboard": "View P . Dashboard",
    "Categories": "View P . Dashboard" // Categories maps to P. Dashboard permission
  };
  
  // Category name mapping for permission checking (different from display)
  const categoryPermissionMap = {
    "Categories": "P . Dashboard" // Categories should check P. Dashboard category permissions
  };
  
  // If category has a permission category mapping, use it
  if (categoryPermissionMap[categoryName]) {
    const permissionCategory = categoryPermissionMap[categoryName];
    const expectedPermissionName = specialCases[categoryName] || categoryName;
    return hasPermission(expectedPermissionName, permissionCategory, userPermissions);
  }
  
  const expectedPermissionName = specialCases[categoryName] || categoryName;
  return hasPermission(expectedPermissionName, categoryName, userPermissions);
};

// Helper function to filter sublinks based on permissions
const filterSublinks = (sublinks, categoryName, userPermissions) => {
  if (!sublinks || sublinks.length === 0) return [];
  
  return sublinks.filter(sublink => {
    return hasPermission(sublink.name, categoryName, userPermissions);
  });
};

// Original Navbar_items definition
const OriginalNavbar_items = {
  Dashboard: {
    main: "Dashboard",
    to: "/",
    icon: HouseLine,
  },
  Categories: {
    main: "Categories",
    to: "/product/categories",
    icon: Gauge,
  },

  Notification: {
    main: "Notification",
    to: path.notification,
    icon: BellRinging,
  },
  Theme: {
    main: "Theme",
    // to: path.reports,
    icon: ChartBar,
    sublinks: [
      {
        name: "Theme",
        to: path.theme,
      },
      {
        name: "Products",
        to: path.theme_products,
      },
      {
        name: "Theme Report",
        to: path.theme_report,
      }
      // Hide "Theme Report" if user is SEO team
      // ...(isSeoTeam ? [] : [
      //   {
      //     name: "Theme Report",
      //     to: path.theme_report,
      //   },
      // ]),
    ],
  },
  Reports: {
    main: "Reports",
    // to: path.reports,
    icon: ChartBar,
    sublinks: [
      {
        name: "Report",
        to: path.reports,
      },
      // {
      //   name: "Daily Reports",
      //   to: path.sales_report,
      // },
      {
        name: "Product Reports",
        to: path.sales_report_dash,
      },
      {
        name: "Sales Reports",
        to: path.sales_report_inv,
      },
      // {
      //   name: "Daily Reports",
      //   to: path.sales_report_optimise,
      // },
      {
        name: "Discount Report",
        to: path.discount_report,
      },
      {
        name: "Area Reports",
        to: path.area_report,
      },
    ],
  },
  Customers: {
    main: "Customers",
    // to: path.customer_list,
    icon: User,
    sublinks: [
      {
        name: "Customer List",
        to: path.customer_list,
      },
      {
        name: "Bulk Import",
        to: path.customer_bulk_import,
      },
    ],
  },
  Outlets: {
    main: "Outlets",
    // to: path.customer_list,
    icon: Storefront,
    sublinks: [
      {
        name: "Outlet List",
        to: path.outlet_list,
      },
      {
        name: "Outlet users",
        to: path.outlet_user_list,
      },
      {
        name: "Outlet Products",
        to: path.outlet_products_list,
      },
      {
        name: "Chalan List",
        to: "/outlet/chalan_list",
      },
      {
        name: "Return Products",
        to: path.return_products_list,
      },
      {
        name: "Internal Return",
        to: "/outlet/outlet_to_outlet_return_list",
      },
      {
        name: "Product Report",
        to: path.outlet_product_report
      },
      {
        name: "Sales Report",
        to: path.outlet_sales_report
      },
      {
        name: "Cash Report",
        to: path.outlet_cash_report
      },
      {
        name: "Discount Report",
        to: path.outlet_discount_report  
      }
    ],
  },
  Promotions: {
    main: "Promotions",
    icon: SpeakerHigh,
    sublinks: [
      {
        name: "Review",
        to: path.review,
      },
      {
        name: "Offer",
        to: path.offer,
      },
      {
        name: "Combo Offer",
        to: path.combooffer,
      },
      // {
      //   name: "Coupon",
      //   to: path.cupons,
      // },
      {
        name: "Voucher",
        to: "/promotions/voucher-list",
      },
      {
        name: "Banner",
        to: path.banner,
      },

      {
        name: "Contact Us",
        to: path.contactUs,
      },
      {
        name: "Work With Us",
        to: path.work_with_us,
      },
    ],
  },

  Web_Settings: {
    main: "Web_Settings",
    icon: ShoppingCartSimple,
    sublinks: [
      {
        name: "Top Banner",
        to: "/website-settings/top-banner-list",
      },
      {
        name: "Nav Settings",
        to: "/website-settings/nav-item-list",
      },
      {
        name: "Hero Banner",
        to: "/website-settings/hero-banner-list",
      },
      {
        name: "Top Category",
        to: "/website-settings/top-best-category-list",
      },
      {
        name: "Arrival Banner",
        to: "/website-settings/custom-new-arrival-banner-list",
      },
      {
        name: "Offer Banner",
        to: "/website-settings/custom-offer-banner-list",
      },
      {
        name: "Theme Banner",
        to: "/website-settings/custom-theme-banner-list",
      },
      {
        name: "Category Banner",
        to: "/website-settings/category-banner-list",
      },
      {
        name: "Festival Banner",
        to: "/website-settings/festival-banner-list",
      },
      {
        name: "Corporate Order",
        to: "/corporate/order-list",
      },
      {
        name: "Blog Tag",
        to: "/website-settings/blog-tag-list",
      },
      {
        name: "Blog Category",
        to: "/website-settings/blog-cat-list",
      },
      {
        name: "Blogs",
        to: "/website-settings/blog-list",
      },
      {
        name: "Custom Review",
        to: "/website-settings/custom-review-list",
      }
    ],
  },
  Custom: {
    main: "Custom",
    // to: path.customer_list,
    icon: ShoppingCartSimple,
    sublinks: [
      {
        name: "Create Invoice",
        to: path.custom_create_invoice,
      },
      {
        name: "invoice List",
        to: path.custom_invoice_list,
      },
      // {
      //   name: "Sales Report",
      //   to: path.custom_sales_report,
      // },
    ],
  },
  Inventory: {
    main: "Inventory",
    icon: Cube,

    sublinks: [
      {
        name: "Add Category",
        to: path.add_category,
      },
      {
        name: "Category List",
        to: path.category_list,
      },
      {
        name: "Add Product",
        to: `/inventory/add-product/0`,
      },
      {
        name: "Home Decor",
        to: path.home_decor_list,
      },
      {
        name: "In Style",
        to: path.product_list,
      },
      {
        name: "Attribute",
        to: path.add_attribute,
      },
      {
        name: "Bulk Import",
        to: path.bulk_import,
      },
      {
        name: "Catalog Export",
        to: path.catalog_export,
      },
    ],
  },
  Combo: {
    main: "Combo",
    icon: Cube,

    sublinks: [
      {
        name: "Add Combo",
        to: "/inventory/add-combo-product",
      },
      {
        name: "Combo List",
        to: "/inventory/combo-product-list",
      },
    ],
  },
  New_Arrival: {
    main: "New_Arrival",
    icon: Waves,

    sublinks: [
      {
        name: "Add Multiple",
        to: path.add_multiple_arrival_product,
      },
      {
        name: "Arrival List",
        to: path.arrival_product_list,
      },
    ],
  },

  Trending: {
    main: "Trending",
    icon: TrendUp,

    sublinks: [
      // {
      //   name: "Add Trending",
      //   to: path.add_trending_product,
      // },
      {
        name: "Add Multiple",
        to: path.add_multiple_trending_product,
      },
      {
        name: "Trending List",
        to: path.trending_product_list,
      },
    ],
  },
  Festival: {
    main: "Festival",
    icon: MagicWand,
    sublinks: [
      {
        name: "Add Multiple",
        to: path.add_festival_product,
      },
      {
        name: "Product List",
        to: path.festival_product_list,
      },
      {
        name: "Festival List",
        to: path.festival_list,
      },
    ],
  },
  Highlighted: {
    main: "Highlighted",
    icon: Waves,

    sublinks: [
      {
        name: "Add Highlighted",
        to: path.add_multiple_highlighted_product,
      },
      {
        name: "Highlighted List",
        to: path.highlighted_product_list,
      },
    ],
  },
  Matching: {
    main: "Matching",
    icon: AlignBottom,
    sublinks: [
      {
        name: "Add Matching",
        to: path.add_matching,
      },
      {
        name: "Matching List",
        to: path.matching_list,
      },
    ],
  },
  Sales: {
    main: "Sales",
    icon: ShoppingCart,
    sublinks: [
      {
        name: "Create Invoice",
        to: path.create_invoice,
      },

      {
        name: "Invoice List",
        to: path.invoice_list,
      },
    ],
  },
  Draft: {
    main: "Draft",
    icon: ShoppingCart,
    sublinks: [
      {
        name: "Create Draft",
        to: path.create_draft_invoice,
      },
      {
        name: "Draft Invoice List",
        to: path.draft_invoice_list,
      },
    ],
  },
  Purchase: {
    main: "Purchase",
    icon: PhosphorLogo,

    sublinks: [
      {
        name: "Create Order",
        to: path.add_purchace_order,
      },

      {
        name: "Order List",
        to: path.purchace_order_list,
      },
      // {
      //   name: "Order Report",
      //   to: path.purchace_order_report,
      // },
    ],
  },
  Requisition: {
    main: "Requisition",
    icon: Factory,
    sublinks: [
      {
        name: "Create Order",
        to: path.add_requisition_order,
      },

      {
        name: "Order List",
        to: path.requisition_order_list,
      },
      // {
      //   name: "Order Report",
      //   to: path.requisition_order_report,
      // },
    ],
  },
  User: {
    main: "User",
    icon: Users,

    sublinks: [
      {
      name: "Role List",
      to: path.role_list,
     },
      {
        name: "Add User",
        to: path.add_user,
      },
      {
        name: "Users List",
        to: path.user_list,
      },
      {
        name: "Website User",
        to: path.web_user_list,
      },
    ],
  },
  Employe: {
    main: "Employe",
    icon: Users,

    sublinks: [
      {
        name: "Add Employe",
        to: path.add_employe,
      },

      {
        name: "Employe List",
        to: path.employe_list,
      },
    ],
  },
  Settings: {
    main: "Settings",
    icon: GearSix,

    sublinks: [
      {
        name: "Global Setting",
        to: path.global_setting,
      },
      {
        name: "Popup Banner",
        to: "/web-settings/pop-up-banner",
      },
      {
        name: "Main Setting",
        to: path.main_setting,
      },
      {
        name: "Navigation Menu",
        to: path.navigation_menu,
      },
      {
        name: "Display Center",
        to: path.display_center,
      },
      {
        name: "Social Media",
        to: path.social_media,
      },
      {
        name: "Testimonials",
        to: path.testimonials,
      },
      {
        name: "Team Member",
        to: path.teammember,
      },
      {
        name: "Events List",
        to: path.events,
      },
      
    ],
  },
};

// Filter Navbar_items based on permissions (now accepts permissions as parameter)
const filterNavbarItems = (items, userPermissions) => {
  const filtered = {};
  
  Object.keys(items).forEach((key) => {
    const item = items[key];
    const categoryName = item.main;
    
    // If item has sublinks
    if (item.sublinks && item.sublinks.length > 0) {
      // Filter sublinks based on permissions
      const filteredSublinks = filterSublinks(item.sublinks, categoryName, userPermissions);
      
      // Only include the category if it has at least one visible sublink
      if (filteredSublinks.length > 0) {
        filtered[key] = {
          ...item,
          sublinks: filteredSublinks
        };
      }
    } 
    // If item is a single item (has 'to' property but no sublinks)
    else if (item.to) {
      // Check if permission exists for this single item
      const hasPerm = hasSingleItemPermission(categoryName, userPermissions);
      if (hasPerm) {
        filtered[key] = item;
      }
    }
  });
  
  return filtered;
};

// Function to get filtered Navbar_items (reads permissions dynamically)
const getFilteredNavbarItems = () => {
  const userPermissions = getCurrentUserPermissions();
  return filterNavbarItems(OriginalNavbar_items, userPermissions);
};

// Export filtered Navbar_items as a getter that reads permissions dynamically
// Using a Proxy to make it behave like an object but read permissions on each access
export const Navbar_items = new Proxy({}, {
  get: function(target, prop) {
    const filtered = getFilteredNavbarItems();
    return filtered[prop];
  },
  ownKeys: function(target) {
    const filtered = getFilteredNavbarItems();
    return Reflect.ownKeys(filtered);
  },
  has: function(target, prop) {
    const filtered = getFilteredNavbarItems();
    return prop in filtered;
  },
  getOwnPropertyDescriptor: function(target, prop) {
    const filtered = getFilteredNavbarItems();
    return Reflect.getOwnPropertyDescriptor(filtered, prop);
  }
});
